import { PlusOutlined } from '@ant-design/icons';
import type { FormListFieldData } from 'antd';
import { Button, Collapse, Form } from 'antd';
import { useContext, useEffect, useState } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useBedCategories } from 'app/hooks/data/rooms/useBedCategories';
import { BED_TASK_CATEGORIES } from 'app/typings';

import { FormLineLayout } from '../../../FormLineLayout/FormLineLayout';

import { BedCostRow, BedPriceRow, BedTypeRow } from './BedForm';
import { BedHeader } from './BedHeader';

const { Panel } = Collapse;

type DefaultBedsProps = {
  fields: FormListFieldData[];
  addBed: ({ included }: { included: boolean }) => void;
  removeBed: (index: number) => void;
};

export const BedsList = ({ fields, addBed, removeBed }: DefaultBedsProps) => {
  const form = Form.useFormInstance();
  const formValues = Form.useWatch([], form);
  const { currencyInput } = useContext(CurrencyContext);
  const { isEditMode } = useContext(ViewEditModeContext);
  const { data: bedCategories = [] } = useBedCategories();
  const [activeKeys, setActiveKeys] = useState<string[]>(
    fields.map((field) => `${field.key}`)
  );

  useEffect(() => {
    setActiveKeys((prevActiveKeys) => {
      const newKey = `${fields[fields.length - 1]?.key}`;
      const index = prevActiveKeys.indexOf(newKey);

      if (index < 0) {
        return [...prevActiveKeys, newKey];
      }

      return prevActiveKeys;
    });
  }, [fields]);

  const toggleActiveKey = (newActiveKeys: string | string[]) => {
    setActiveKeys(
      typeof newActiveKeys === 'string' ? [newActiveKeys] : newActiveKeys
    );
  };

  const isTaskCategory = (categoryId: number) =>
    BED_TASK_CATEGORIES.includes(categoryId);

  return (
    <div className="default-beds">
      <div className="bed-title-section">Default beds</div>
      <Collapse
        activeKey={activeKeys}
        onChange={toggleActiveKey}
        className="bed-collapse"
        bordered={false}
      >
        {fields.map(({ key, name, ...restField }, index) => {
          const categoryId = formValues?.beds?.[index]?.categoryId;

          if (isTaskCategory(categoryId)) {
            return null;
          }

          return (
            <Panel
              header={
                <BedHeader index={index} name={name} onRemove={removeBed} />
              }
              className="bed-panel"
              key={key}
            >
              <FormLineLayout key={name} label="Type" isRequired={true}>
                <Form.Item shouldUpdate noStyle>
                  {isEditMode ? (
                    <BedTypeRow
                      bedCategories={bedCategories}
                      index={index}
                      getFieldValue={form.getFieldValue}
                      name={[`${name}`, 'categoryId']}
                      restField={restField}
                    />
                  ) : (
                    <>
                      {bedCategories.find((cat) => cat.id === categoryId)?.name}
                    </>
                  )}
                </Form.Item>
              </FormLineLayout>
              {index > 0 && (
                <>
                  <BedCostRow restField={restField} name={[name]} />
                  <Form.Item shouldUpdate noStyle>
                    {({ getFieldValue }) =>
                      getFieldValue(['beds', name, 'included']) === false && (
                        <BedPriceRow
                          restField={restField}
                          name={[name]}
                          currencyInput={currencyInput}
                        />
                      )
                    }
                  </Form.Item>
                </>
              )}
            </Panel>
          );
        })}
      </Collapse>
      {isEditMode && (
        <div className="footer">
          <Button
            icon={<PlusOutlined />}
            onClick={() => addBed({ included: true })}
          >
            Add bed
          </Button>
        </div>
      )}
    </div>
  );
};
